import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, selectIsAuthenticated, selectPermissions, selectPermissionsLoading } from '../app/store/slices/authSlice';
import { getCurrentUser } from '../services/authService';
import { setUser, setPermissions, setPermissionsLoading } from '../app/store/slices/authSlice';
import { getUserPermissions } from '../features/access-management/services/accessManagementService';

const AuthContext = createContext(null);

// ✅ Define all roles in your system
export const ROLES = {
  SUPER_ADMIN: 'SuperAdmin',
  COMPANY_ADMIN: 'CompanyAdmin',
  MANAGER: 'Manager',
  USER: 'User',
  READ_ONLY_USER: 'ReadOnlyUser',
};

// ✅ Role hierarchy and permissions mapping
export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 5,    // Highest access - platform admin
  [ROLES.COMPANY_ADMIN]: 4,  // Company-wide access
  [ROLES.MANAGER]: 3,         // Store manager
  [ROLES.USER]: 2,            // Regular store user
  [ROLES.READ_ONLY_USER]: 1, // View-only access
};

// ✅ Permission groups
export const PERMISSIONS = {
  // Company Management (SuperAdmin only)
  MANAGE_COMPANIES: [ROLES.SUPER_ADMIN],
  VIEW_ALL_COMPANIES: [ROLES.SUPER_ADMIN],
  DELETE_COMPANY: [ROLES.SUPER_ADMIN],
  
  // Store Management
  MANAGE_STORES: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN],
  VIEW_ALL_STORES: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN],
  
  // User Management
  MANAGE_USERS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_USERS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  
  // Invoices
  CREATE_INVOICE: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER],
  EDIT_INVOICE: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER],
  DELETE_INVOICE: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_INVOICE: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.READ_ONLY_USER],
  
  // Products
  CREATE_PRODUCT: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  EDIT_PRODUCT: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  DELETE_PRODUCT: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_PRODUCT: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.READ_ONLY_USER],
  
  // Customers
  CREATE_CUSTOMER: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER],
  EDIT_CUSTOMER: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER],
  DELETE_CUSTOMER: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_CUSTOMER: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.READ_ONLY_USER],
  
  // Reports
  VIEW_REPORTS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_FINANCIAL_REPORTS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN],
  
  // Settings
  MANAGE_COMPANY_SETTINGS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN],
  MANAGE_STORE_SETTINGS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_SETTINGS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  
  // Delivery Notes
  CREATE_DELIVERY_NOTE: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER],
  EDIT_DELIVERY_NOTE: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER],
  DELETE_DELIVERY_NOTE: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_DELIVERY_NOTE: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.READ_ONLY_USER],
  
  // Purchase Orders
  CREATE_PURCHASE_ORDER: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  EDIT_PURCHASE_ORDER: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  DELETE_PURCHASE_ORDER: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_PURCHASE_ORDER: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.READ_ONLY_USER],
  
  // POS
  ACCESS_POS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER],
  
  // Payments
  MANAGE_PAYMENTS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER],
  VIEW_PAYMENTS: [ROLES.SUPER_ADMIN, ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.USER, ROLES.READ_ONLY_USER],
};

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const userFromRedux = useSelector(selectUser);
  const isAuthenticatedFromRedux = useSelector(selectIsAuthenticated);
  const permissionsFromRedux = useSelector(selectPermissions);
  const permissionsLoadingFromRedux = useSelector(selectPermissionsLoading);
  const [loading, setLoading] = useState(true);
  const [localUser, setLocalUser] = useState(null);

  // ✅ Fetch user's module permissions from API (only if not already in Redux)
  const fetchUserPermissions = async (userId) => {
    if (!userId) return;
    
    // ✅ Check if permissions already exist in Redux
    if (permissionsFromRedux && Object.keys(permissionsFromRedux).length > 0) {
      return;
    }
    
    // ✅ Check if permissions are already being loaded (prevent duplicate calls)
    if (permissionsLoadingFromRedux) {
      return;
    }
    
    try {
      dispatch(setPermissionsLoading(true));
      const response = await getUserPermissions(userId);
      const perms = response?.payload || response?.permissions || {};
      dispatch(setPermissions(perms));
    } catch (error) {
      console.error('❌ Error loading user permissions:', error);
      dispatch(setPermissions({}));
    } finally {
      dispatch(setPermissionsLoading(false));
    }
  };

  useEffect(() => {
    // ✅ Initialize user from localStorage if not in Redux (NO API CALLS HERE)
    const initializeUser = () => {
      try {
        const user = getCurrentUser();
        
        if (user) {
          // Store in local state immediately
          setLocalUser(user);
          
          // Ensure it's in Redux
          if (!userFromRedux) {
            dispatch(setUser(user));
          }
          
          // ✅ Keep loading true if user exists - AuthInitializer will handle permissions
          // The combined loading state (loading || permissionsLoadingFromRedux) will be managed
        } else {
          // No user - safe to set loading to false immediately
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Error initializing user:', error);
        setLoading(false);
      }
    };

    initializeUser();
  }, [dispatch, userFromRedux]);

  // ✅ Set loading to false once permissions are loaded OR if no user
  useEffect(() => {
    const currentUser = userFromRedux || localUser;
    if (!currentUser) {
      setLoading(false);
    } else if (permissionsFromRedux && Object.keys(permissionsFromRedux).length > 0) {
      // Permissions are loaded, safe to set loading to false
      setLoading(false);
    } else if (!permissionsLoadingFromRedux) {
      // Not loading permissions and no permissions exist - this handles the case
      // where we're on a public route and don't need permissions
      setLoading(false);
    }
  }, [userFromRedux, localUser, permissionsFromRedux, permissionsLoadingFromRedux]);

  // ✅ Use Redux user if available, otherwise use local user from localStorage
  const currentUser = userFromRedux || localUser;
  // ✅ Extract role from user object (check both role and Role for case sensitivity)
  const role = currentUser?.role || currentUser?.Role || 'User';
  
  // ✅ Debug: Log user and role info
  useEffect(() => {
    if (currentUser) {
    } else {
    }
  }, [currentUser, role, userFromRedux]);

  // ✅ Check if user has a specific role
  const hasRole = (requiredRole) => {
    if (!role) return false;
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(role);
    }
    return role === requiredRole;
  };

  // ✅ Check if user has any of the provided roles
  const hasAnyRole = (...roles) => {
    if (!role) return false;
    return roles.some(r => role === r);
  };

  // ✅ Check if user has permission based on permission key
  const hasPermission = (permissionKey) => {
    if (!role) return false;
    // ✅ First try hardcoded permissions (for backward compatibility)
    const allowedRoles = PERMISSIONS[permissionKey];
    if (allowedRoles && allowedRoles.includes(role)) {
      return true;
    }
    return false;
  };

  // ✅ NEW: Check module-level permission (CanView, CanEdit, CanCreate, CanDelete)
  const hasModulePermission = (moduleName, permissionType = 'CanView') => {
    if (!moduleName) return false;
    
    // SuperAdmin and CompanyAdmin bypass checks
    if (isSuperAdmin || isCompanyAdmin) return true;
    
    // ✅ Use permissions from Redux store
    const modulePerms = permissionsFromRedux[moduleName];
    if (!modulePerms) return false;
    
    return modulePerms[permissionType] === true;
  };

  // ✅ NEW: Check if user can view a module
  const canViewModule = (moduleName) => hasModulePermission(moduleName, 'CanView');
  const canEditModule = (moduleName) => hasModulePermission(moduleName, 'CanEdit');
  const canCreateModule = (moduleName) => hasModulePermission(moduleName, 'CanCreate');
  const canDeleteModule = (moduleName) => hasModulePermission(moduleName, 'CanDelete');

  // ✅ Check if user role is higher or equal in hierarchy
  const hasMinimumRole = (minimumRole) => {
    if (!role) return false;
    const userRoleLevel = ROLE_HIERARCHY[role] || 0;
    const minimumRoleLevel = ROLE_HIERARCHY[minimumRole] || 0;
    return userRoleLevel >= minimumRoleLevel;
  };

  // ✅ Role-specific boolean flags for convenience
  const isSuperAdmin = role === ROLES.SUPER_ADMIN;
  const isCompanyAdmin = role === ROLES.COMPANY_ADMIN;
  const isManager = role === ROLES.MANAGER;
  const isUser = role === ROLES.USER;
  const isReadOnlyUser = role === ROLES.READ_ONLY_USER;

  // ✅ Check if user can modify data (not read-only)
  const canModify = !isReadOnlyUser;

  // ✅ Check if user can access admin features
  const isAdmin = isSuperAdmin || isCompanyAdmin;

  // ✅ Check if user can manage store-level operations
  const canManageStore = isSuperAdmin || isCompanyAdmin || isManager;

  const value = {
    user: currentUser,
    role,
    loading: loading || permissionsLoadingFromRedux,
    isAuthenticated: isAuthenticatedFromRedux || !!currentUser,
    
    // Module Permissions (NEW) - from Redux store
    modulePermissions: permissionsFromRedux,
    hasModulePermission,
    canViewModule,
    canEditModule,
    canCreateModule,
    canDeleteModule,
    
    // Role checking functions
    hasRole,
    hasAnyRole,
    hasPermission,
    hasMinimumRole,
    
    // Boolean flags
    isSuperAdmin,
    isCompanyAdmin,
    isManager,
    isUser,
    isReadOnlyUser,
    canModify,
    isAdmin,
    canManageStore,
    
    // Role hierarchy level
    roleLevel: ROLE_HIERARCHY[role] || 0,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
